import { ReactNode } from 'react'

import { TableRowProps } from '@chakra-ui/react'
import { TableCellProps } from '@chakra-ui/react'
import { TableColumnHeaderProps } from '@chakra-ui/react'

export enum GeneralTableKeys {
  VARIABLE_NAME = 'variableName',
  ASSUMPTION_SET_NAME = 'assumptionSetName',
  STATE_NAME = 'stateName',
  CURRENT_STATUS = 'currentStatus',
  PREVIOUS_STATUS = 'previousStatus',
  SELECT = 'select',
  UNIT = 'unit',
}

export enum NumericTableKeys {
  EXP = 'exp',
  RISK_RATIO = 'riskRatio',
  PREDICTED_RELATIVE_RISK = 'predictedRelativeRisk',
  EXPECTATION_WITHOUT_ASSUMPTION = 'expectationWithoutAssumption',
  EXPECTATION_WITH_BASE_POPULATION = 'expectationWithBasePopulation',
  SENSITIVITY = 'sensitivity',
  SPECIFICITY = 'specificity',
  INVERSE_RISK_RATIO = 'inverseRiskRatio',
  TOL = 'tol',
  PRED = 'pred',
  ASSUMPTION_CONTINUOUS_VALUE = 'assumptionContinuousValue',
  ERR = 'err',
  WERR = 'werr',
  WCHG = 'wchg',
  PCHG = 'pchg',
  LOTP = 'lotp',
  MAX_WERR = 'maxWerr',
  MAX_ERR = 'maxErr',
  PREVIOUS_PREDICTION = 'previousPrediction',
  CURRENT_PREDICTION = 'currentPrediction',
  PREVIOUS_ERROR = 'previousError',
  CURRENT_ERROR = 'currentError',
  ABSOLUTE_ERROR_DIFFERENCE = 'absoluteErrorDifference',
  PREDICTION_DIFFERENCE = 'predictionDifference',
  PROBABILITY = 'probability',
  DIAGNOSTIC_VALUE = 'diagnosticValue',
  IMPACT_RELATIVE = 'impactRelative',
  IMPACT_ABSOLUTE = 'impactAbsolute',
}

export type Title = {
  key: Partial<GeneralTableKeys> | Partial<NumericTableKeys>
  element?: ReactNode | string
  sortOptions?: SortOptions & { default?: boolean }
  tableHeaderColumnProps?: TableColumnHeaderProps
}
export type Row = {
  id: string
  variableId?: string
  stateId?: string
  rowLabel?: string
  columns: Partial<{
    [key in GeneralTableKeys | NumericTableKeys]: {
      value: string | number
      element?: ReactNode | string
      cellProps?: TableCellProps
    }
  }>
  rowProps?: TableRowProps
  expandableElement?: ReactNode
  hasExpandableContent?: boolean
}

export type SortOptions = {
  primaryKey: GeneralTableKeys | NumericTableKeys
  reversed?: boolean
  secondaryKey?: GeneralTableKeys | NumericTableKeys
  groupByVariableOnPivotedAssumptionSets?: boolean
}

export type SortableRow = {
  columns: Partial<{
    [key in GeneralTableKeys | NumericTableKeys]: { value: string | number }
  }>
}

export type SortKeys = keyof SortableRow['columns']

export type VirtualizationConfig = {
  rowHeight: number
  overscan?: number
}

export type SimpleTableProps = {
  rows: Array<Row>
  titles: Array<Title>
  tableLabel?: string
  selectedRowId?: string
  pagination?: {
    pageSize: number
    showPageText?: boolean
  }
  enableColumnReorder?: boolean
  onColumnReorder?: (newOrder: (GeneralTableKeys | NumericTableKeys)[]) => void
  virtualized?: VirtualizationConfig
}

export const EXPANDABLE_COLUMNS = new Set([
  GeneralTableKeys.ASSUMPTION_SET_NAME,
  GeneralTableKeys.VARIABLE_NAME,
])
